import {
    type Upload,
    isUsingDisk,
    shouldProcess,
    type UploadGroupId,
    type UploadStatus,
    type UploadWritingFileToDisk,
    type UploadPendingSendFileMeta,
    type UploadUploadingFile,
    type UploadSendingFileMeta,
    type UploadPendingRetryUploadFile,
    uploadStatuses,
    hasUnsyncedData,
} from './Upload'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { getNextRetry } from './getNextRetry'
import { UploadSubscriptionManager } from './UploadSubscriptionManager'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileUploadData } from './FileUploadData'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'

export abstract class UploadQueue {
    protected uploadSubscriptionManager = new UploadSubscriptionManager()

    constructor(
        private concurrentLimit: number,
        private shouldRetry: boolean = true
    ) {}

    public subscribe = (fileId: FileId, callback: (upload: Upload) => void) => {
        this.uploadSubscriptionManager.subscribe(fileId, callback)
    }

    public unsubscribe = (fileId: FileId, callback: (upload: Upload) => void) => {
        this.uploadSubscriptionManager.unsubscribe(fileId, callback)
    }

    public subscribeToGroup = (groupId: UploadGroupId, callback: (uploads: Upload[]) => void) => {
        this.uploadSubscriptionManager.subscribeToGroup(groupId, callback)
    }

    public unsubscribeToGroup = (groupId: UploadGroupId, callback: (uploads: Upload[]) => void) => {
        this.uploadSubscriptionManager.unsubscribeToGroup(groupId, callback)
    }

    public subscribeToAll = (callback: (uploads: Upload) => void) => {
        this.uploadSubscriptionManager.subscribeToAll(callback)
    }

    public unsubscribeToAll = (callback: (uploads: Upload) => void) => {
        this.uploadSubscriptionManager.unsubscribeToAll(callback)
    }

    private makeUpdateFunction<T extends Upload>(validStatuses: UploadStatus[], callback: (upload: T) => Upload) {
        const validateStatus = (upload: Upload): upload is T => validStatuses.includes(upload.status)
        return async (fileId: FileId, logger: ClientLogger) => {
            const upload = await this.getUpload(fileId)

            logger.log('Validating upload status', { fileId, status: upload?.status })

            if (!upload) throw new Error(`Upload with id ${fileId} does not exist`)
            if (upload.status === 'UploadCancelled') return // Do nothing for cancelled uploads
            if (!validateStatus(upload)) {
                logger.error('Upload status is not valid for this operation', { fileId, upload })
                throw new Error(`Upload status cannot be updated from ${upload.status}`)
            }

            const updatedUpload = callback(upload)
            await this.setUploadState(updatedUpload)

            logger.log('Completed validating upload status and updated upload', {
                fileId,
                originalStatus: upload?.status,
                updatedStatus: updatedUpload?.status,
            })

            const groupUploads = updatedUpload.groupId ? await this.getUploads(updatedUpload.groupId) : []
            this.uploadSubscriptionManager.triggerSubscribers(updatedUpload, groupUploads)
        }
    }

    public addUpload = async (
        fileUploadData: FileUploadData,
        acceptedExtensions: FileExtension[],
        sizeLimitInBytes: number = 1_000_000_000,
        customer: number,
        groupId?: UploadGroupId
    ) => {
        const upload = this.getInitialUploadState(
            fileUploadData,
            acceptedExtensions,
            sizeLimitInBytes,
            customer,
            groupId
        )
        await this.setUploadState(upload)
        const groupUploads = upload.groupId ? await this.getUploads(upload.groupId) : []
        this.uploadSubscriptionManager.triggerSubscribers(upload, groupUploads)
    }

    public abstract getUpload: (fileId: FileId) => Promise<Upload>

    public abstract getUploads: (groupId?: UploadGroupId) => Promise<Upload[]>

    public async hasUnsyncedData() {
        const uploads = await this.getUploads()
        return uploads.some(hasUnsyncedData)
    }

    public failedToWriteToDisk = this.makeUpdateFunction<UploadWritingFileToDisk>(
        ['UploadWritingFileToDisk'],
        (upload) => {
            return {
                fileUploadData: upload.fileUploadData,
                groupId: upload.groupId,
                customer: upload.customer,
                status: 'UploadWriteToDiskFailed',
                canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
            }
        }
    )

    public writeToDiskComplete = this.makeUpdateFunction<UploadWritingFileToDisk>(
        ['UploadWritingFileToDisk'],
        (upload) => {
            return {
                fileUploadData: upload.fileUploadData,
                groupId: upload.groupId,
                customer: upload.customer,
                status: 'UploadPendingSendFileMeta',
                failCount: 0,
                canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
            }
        }
    )

    public startingUploadFileMeta = this.makeUpdateFunction<UploadPendingSendFileMeta>(
        ['UploadPendingSendFileMeta'],
        (upload) => {
            return {
                fileUploadData: upload.fileUploadData,
                customer: upload.customer,
                groupId: upload.groupId,
                status: 'UploadSendingFileMeta',
                canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
            }
        }
    )

    public uploadFailed = (fileId: FileId, failCount: number = 1, logger: ClientLogger) =>
        this.makeUpdateFunction<UploadUploadingFile>(['UploadUploadingFile'], (upload) => {
            return this.getNextFailureState(upload, failCount)
        })(fileId, logger)

    public sendingFileMetaFailed = (fileId: FileId, failCount: number = 1, logger: ClientLogger) =>
        this.makeUpdateFunction<UploadSendingFileMeta>(['UploadSendingFileMeta'], (upload) => {
            return this.getNextFailureState(upload, failCount)
        })(fileId, logger)

    private getNextFailureState = (upload: Upload, failCount: number): Upload => {
        const nextRetry = getNextRetry(failCount)

        if (!nextRetry || !this.shouldRetry) {
            return {
                fileUploadData: upload.fileUploadData,
                groupId: upload.groupId,
                customer: upload.customer,
                status: 'UploadFailed',
                canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
            }
        }

        const uploadBase = {
            fileUploadData: upload.fileUploadData,
            groupId: upload.groupId,
            customer: upload.customer,
            failCount,
            nextRetry,
            canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
        }

        if (upload.status === 'UploadSendingFileMeta') {
            return {
                status: 'UploadPendingSendFileMeta',
                ...uploadBase,
            }
        }

        return {
            status: 'UploadPendingRetryUploadFile',
            ...uploadBase,
        }
    }

    public startingFileUpload = this.makeUpdateFunction<UploadPendingRetryUploadFile>(
        ['UploadPendingRetryUploadFile', 'UploadSendingFileMeta'],
        (upload) => {
            return {
                fileUploadData: upload.fileUploadData,
                groupId: upload.groupId,
                customer: upload.customer,
                status: 'UploadUploadingFile',
                bytesUploaded: 0,
                canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
            }
        }
    )

    public uploadComplete = this.makeUpdateFunction<UploadUploadingFile>(['UploadUploadingFile'], (upload) => {
        return {
            fileUploadData: upload.fileUploadData,
            groupId: upload.groupId,
            customer: upload.customer,
            status: 'Uploaded',
            canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
        }
    })

    public updateProgress = (fileId: FileId, bytesUploaded: number, logger: ClientLogger) =>
        this.makeUpdateFunction<UploadUploadingFile>(['UploadUploadingFile'], (upload) => {
            return {
                ...upload,
                bytesUploaded,
            }
        })(fileId, logger)

    public getUploadsToProcess = async () => {
        const uploads = await this.getUploads()
        const uploadsUsingDisk = uploads.filter(isUsingDisk)
        const uploadsToProcessCount = this.concurrentLimit - uploadsUsingDisk.length
        if (uploadsToProcessCount < 1) return []
        // Get the processable uploads sorted by oldest first
        return uploads
            .filter(shouldProcess)
            .sort((a, b) => a.fileUploadData.createdAt.getTime() - b.fileUploadData.createdAt.getTime())
            .splice(0, uploadsToProcessCount)
    }

    public cancelUpload = this.makeUpdateFunction<Upload>(uploadStatuses, (upload) => {
        if (upload.status === 'Uploaded') return upload
        return {
            fileUploadData: upload.fileUploadData,
            groupId: upload.groupId,
            customer: upload.customer,
            status: 'UploadCancelled',
            canBeDeletedWhenDone: upload.canBeDeletedWhenDone,
        }
    })

    protected abstract setUploadState: (upload: Upload) => Promise<void>

    protected abstract getInitialUploadState: (
        fileUploadData: FileUploadData,
        acceptedExtensions: FileExtension[],
        sizeLimitInBytes: number,
        customer: number,
        groupId?: UploadGroupId
    ) => Upload
}
